/**
 * CartUpsell custom element
 *
 * Handles swapping the current cart item for an upgraded variant when
 * the user clicks an upsell card. After the swap it dispatches a
 * 'cart:update' event so the CartItemsComponent re-renders the drawer
 * without a full page reload.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick.bind(this));
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    // Only respond to clicks on the explicit switch button
    const btn = /** @type {HTMLElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('.cart-upsell__switch-btn[data-upsell-variant]')
        : null
    );

    if (!btn || btn.hasAttribute('disabled')) return;

    const newVariantId = /** @type {HTMLElement} */ (btn).dataset.upsellVariant;
    const removeVariantId = /** @type {HTMLElement} */ (btn).dataset.removeVariant;

    if (!newVariantId || !removeVariantId) return;

    this.#performUpsell(/** @type {HTMLButtonElement} */ (btn), newVariantId, removeVariantId);
  }

  /**
   * Swaps the current variant for the selected upgrade.
   * @param {HTMLButtonElement} btn - The clicked upsell button.
   * @param {string} newVariantId - Variant ID to add to the cart.
   * @param {string} removeVariantId - Variant ID to remove from the cart.
   */
  async #performUpsell(btn, newVariantId, removeVariantId) {
    btn.disabled = true;
    btn.classList.add('cart-upsell__switch-btn--loading');

    try {
      // ── 1. Fetch the current cart ──────────────────────────────────────
      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      const lineIndex = cart.items.findIndex(
        (item) => item.variant_id.toString() === removeVariantId.toString()
      );

      if (lineIndex === -1) {
        console.warn('[CartUpsell] Variant to remove not found in cart:', removeVariantId);
        return;
      }

      const line = lineIndex + 1; // Shopify cart lines are 1-indexed

      // ── 2. Remove the current variant ─────────────────────────────────
      const removeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: 0 }),
      });

      const removeData = await removeRes.json();

      if (removeData.errors) {
        console.error('[CartUpsell] Failed to remove item:', removeData.errors);
        return;
      }

      // ── 3. Add the upgrade variant ─────────────────────────────────────
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(newVariantId, 10), quantity: 1 }),
      });

      const addData = await addRes.json();

      if (addData.status === 422 || addData.errors) {
        console.error('[CartUpsell] Failed to add item:', addData.description ?? addData.errors);
        return;
      }

      // ── 4. Trigger re-render of all cart-items-components ─────────────
      //
      // CartItemsComponent listens for 'cart:update'. When the event's
      // detail.data has no `sections` key for the component's sectionId,
      // it falls back to sectionRenderer.renderSection() which fetches
      // fresh section HTML and morphs the drawer — no page reload needed.
      //
      // CartIcon also listens for 'cart:update' and uses `itemCount` to
      // update the bubble count in the header.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              itemCount: addData.item_count ?? cart.item_count,
              sections: {},
            },
          },
        })
      );
    } catch (error) {
      console.error('[CartUpsell] Unexpected error:', error);
    } finally {
      // Re-enable the button; the morph will replace the DOM anyway.
      btn.disabled = false;
      btn.classList.remove('cart-upsell__switch-btn--loading');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
